import { useEffect } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { useAuthStore } from '@/shared/state/authstore'
import {
  getNotificationsConnection,
  startNotificationsConnection,
  stopNotificationsConnection,
} from '@/shared/realtime/notificationsConnection'
import { notificationKeys } from './keys'
import { isSosEvent } from './sosEvents'

type ReceivedEvent = {
  id: number
  accountId: number
  event: string
  priority: string
  title: string
  body: string | null
  payload: string | null
  sourceId: number | null
  createdOn: string
}

const INCIDENT_EVENTS = new Set(['IncidentSubmitted', 'IncidentStatusChanged', 'IncidentFollowUpAdded'])

export function useNotificationStream(): void {
  const queryClient = useQueryClient()
  const accessToken = useAuthStore((s) => s.accessToken)
  const accountId = useAuthStore((s) => s.accountId)

  useEffect(() => {
    if (!accessToken || accountId == null) return

    const connection = getNotificationsConnection()
    const invalidate = () => {
      void queryClient.invalidateQueries({ queryKey: notificationKeys.all })
    }

    const onReceived = (n: ReceivedEvent) => {
      if (n.accountId !== accountId) return
      invalidate()
      if (INCIDENT_EVENTS.has(n.event) || isSosEvent(n.event)) return
      if (n.priority === 'Critical') {
        toast.error(n.title, { description: n.body ?? undefined, duration: Infinity })
      } else {
        toast(n.title, { description: n.body ?? undefined })
      }
    }

    connection.on('notificationReceived', onReceived)
    connection.on('notificationAcknowledged', invalidate)
    connection.on('notificationEscalated', invalidate)
    connection.onreconnected(invalidate)

    void startNotificationsConnection()

    return () => {
      connection.off('notificationReceived', onReceived)
      connection.off('notificationAcknowledged', invalidate)
      connection.off('notificationEscalated', invalidate)
    }
  }, [accessToken, accountId, queryClient])

  useEffect(() => {
    return () => {
      void stopNotificationsConnection()
    }
  }, [])
}
